import type { FunctionalComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { SafeImage } from '@/components/safe-image.tsx';
import { NavBack } from '@/components/nav-back.tsx';
import { LoadingSpinner } from '@/components/loading-spinner.tsx';
import { MSG } from '@/lib/constants.ts';
import { fetchBanks, fetchWithdrawInfo, saveWithdrawAccount } from '@/lib/withdraw.api.ts';
import { bankLabel, bankLogo, sortBanks } from '@/data/withdraw.ts';
import { digits, returnUrl } from '@/lib/utils.ts';
import { askAlert } from '@/store/dialog.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import type { WithdrawBank } from '@/types/withdraw.ts';
import type { PageProps } from '@/types/props.ts';

const NOTICE = '본인인증 정보와 계좌 정보가 일치하지 않을 경우, 포인트 지급이 제한될 수 있습니다.';

/**
 * 포인트 출금 계좌 등록 (시안 `account-register.html`).
 *
 * ★ 은행 목록을 화면에 박아 두지 않는다. 시안에는 표준 은행코드(국민=004)가 적혀
 *   있지만 이 서비스가 쓰는 코드는 **내부 코드**(국민=3)라 서로 다르다. 서버가 주는
 *   코드만 계좌 등록에 쓸 수 있다.
 */
const MypageAccount: FunctionalComponent<PageProps> = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const userSeq = Number(user?.userSeq ?? 0);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [banks, setBanks] = useState<WithdrawBank[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const [bank, setBank] = useState<WithdrawBank | null>(null);
  const [account, setAccount] = useState('');
  const [holder, setHolder] = useState('');

  /** 이미 등록돼 있으면 '변경' 이다. 계좌번호는 서버가 안 주므로 다시 받는다. */
  const [registered, setRegistered] = useState<string | null>(null);

  const canSubmit = !!bank && account.length >= 8 && holder.trim() !== '' && !busy;

  useEffect(() => {
    if (userSeq <= 0) {
      route(`/login?r=${encodeURIComponent(returnUrl())}`);
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const [bankList, info] = await Promise.all([
          fetchBanks(userSeq),
          fetchWithdrawInfo(userSeq),
        ]);
        if (cancelled) return;

        // 시안 순서로 세우고 시안에 없는 곳은 감춘다.
        if (bankList.result) setBanks(sortBanks(bankList.data?.banks ?? []));

        const current = info.result ? info.data?.account : null;
        if (current) {
          setRegistered(current.label);
          // 예금주는 가려진 값이라 그대로 쓸 수 없다. 은행만 골라 둔다.
          setBank({ code: current.bankCode, name: current.bankName ?? '' });
        } else if (user?.name) {
          // 본인 이름을 미리 채워 둔다. 계좌 예금주와 다르면 고치면 된다.
          setHolder(user.name);
        }
      } catch {
        if (!cancelled) await dispatch(askAlert(MSG.apiFail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userSeq, user?.name, dispatch]);

  async function submit() {
    if (!canSubmit || !bank) return;

    setBusy(true);
    try {
      const response = await saveWithdrawAccount(userSeq, bank.code, account, holder.trim());
      if (!response.result) {
        await dispatch(askAlert(response.message ?? MSG.apiFail));
        return;
      }

      await dispatch(askAlert('출금 계좌를 등록했습니다.'));
      route('/mypage/withdraw', true);
    } catch {
      await dispatch(askAlert(MSG.apiFail));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingSpinner show />;

  return (
    <div class="page">
      <header class="title">
        <NavBack onBack={() => route('/mypage')} />
        <span class="nav-title">포인트 출금 계좌 {registered ? '변경' : '등록'}</span>
      </header>

      <div class="section">
        <div class="contents">
          <div class="edit-form">
            {registered && (
              <div class="form-notice">
                <span class="form-notice-icon">
                  <img src="/images/icon-notice.png" alt="" />
                </span>
                지금 등록된 계좌는 <strong>{registered}</strong> 입니다.
              </div>
            )}

            <div class="edit-field">
              <label class="edit-field-label">은행 선택</label>
              <button type="button" class="bank-select-btn" onClick={() => setPickerOpen(true)}>
                <span class="bank-select-value">
                  {bank ? bankLabel(bank.name) : '은행을 선택해주세요'}
                </span>
                <span class="bank-select-chevron">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </span>
              </button>
            </div>

            <div class="edit-field">
              <label class="edit-field-label">계좌번호</label>
              <input
                class="form-input form-input--full"
                type="text"
                inputMode="numeric"
                maxLength={20}
                placeholder="계좌번호를 입력해주세요 ('-' 없이)"
                value={account}
                onInput={(e) => setAccount(digits(e.currentTarget.value).slice(0, 20))}
              />
            </div>

            <div class="edit-field">
              <label class="edit-field-label">예금주명</label>
              <div class="form-input-clear-wrap">
                <input
                  class="form-input form-input--full"
                  type="text"
                  maxLength={30}
                  placeholder="예금주명을 입력해주세요"
                  value={holder}
                  onInput={(e) => setHolder(e.currentTarget.value)}
                />
                {holder !== '' && (
                  <button
                    type="button"
                    class="input-clear-btn"
                    aria-label="지우기"
                    onClick={() => setHolder('')}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M18 6L6 18" />
                      <path d="M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div class="form-notice">
              <span class="form-notice-icon">
                <img src="/images/icon-notice.png" alt="" />
              </span>
              {NOTICE}
            </div>
          </div>
        </div>
      </div>

      <div class="edit-actions">
        <button
          type="button"
          class="btn-submit"
          disabled={!canSubmit}
          onClick={() => void submit()}
        >
          계좌 {registered ? '변경' : '등록'}하기
        </button>
      </div>

      {/* 은행 선택 */}
      <div class={pickerOpen ? 'popup-overlay show' : 'popup-overlay'}>
        <div class="popup-sheet popup-sheet--lg">
          <div class="popup-body">
            <div class="popup-title-row">
              <h3 class="popup-title">은행 선택</h3>
              <button
                type="button"
                class="popup-title-close-btn"
                aria-label="닫기"
                onClick={() => setPickerOpen(false)}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M18 6L6 18" />
                  <path d="M6 6l12 12" />
                </svg>
              </button>
            </div>

            <ul class="bank-list">
              {banks.map((item) => (
                <li key={item.code}>
                  <button
                    type="button"
                    class="bank-list-item"
                    onClick={() => {
                      setBank(item);
                      setPickerOpen(false);
                    }}
                  >
                    {/*
                      ★ img 는 반드시 `.bank-list-item` 의 **직계 자식**이어야 한다.
                        시안 CSS 가 `.bank-list-item img` 를 48px 원형으로 그린다.
                        감싸는 요소를 하나 두었더니 그 크기에 눌려 작게 나왔다.
                    */}
                    {bankLogo(item.name) ? (
                      <SafeImage
                        src={bankLogo(item.name)}
                        alt=""
                        fallback={
                          <span class="bank-list-blank">{bankLabel(item.name).slice(0, 1)}</span>
                        }
                      />
                    ) : (
                      // 로고가 없는 곳은 같은 크기의 원으로 자리를 채운다.
                      <span class="bank-list-blank">{bankLabel(item.name).slice(0, 1)}</span>
                    )}
                    {bankLabel(item.name)}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MypageAccount;
